import React, { useEffect, useState } from "react";
import { GetStorageProfileResponse, ResponseStatus } from "target_wasm";
import { SectionRule } from "components/common/SectionRule";
import { Badge } from "components/common/Badge";
import { QuotaProgressBar } from "@/components/main/utils/QuotaProgressBar";
import { storageServiceApi } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
import { formatDayMonthYear } from "@/lib/dates";
import {
  fetchCurrentPlan,
  type CurrentPlan,
  type Org,
} from "@/lib/api/organizations";

const log = createLogger("PlanUsagePanel");

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex min-h-8 items-center gap-2 px-2">
    <span className="w-33 shrink-0 text-fg-muted-medium">{label}</span>
    {children}
  </div>
);

/// The plan is the org's (S4a); usage is the org counter the sidebar's quota
/// widget reads. Seats deliberately carry no bar: nothing enforces a seat cap
/// yet, and a bar with no denominator would invent one.
export const PlanUsagePanel: React.FC<{
  org: Org;
  memberCount: number;
}> = ({ org, memberCount }) => {
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [storage, setStorage] = useState<GetStorageProfileResponse | null>(
    null,
  );

  useEffect(() => {
    setPlan(null);
    setPlanLoaded(false);
    void fetchCurrentPlan(org.org_id).then((p) => {
      setPlan(p);
      setPlanLoaded(true);
    });
  }, [org.org_id]);

  useEffect(() => {
    setStorage(null);
    let cancelled = false;
    void (async () => {
      try {
        const response = await storageServiceApi.get("get_storage_profile", {
          params: { org_id: org.org_id },
        });
        const data = new GetStorageProfileResponse(
          JSON.stringify(response.data),
        );
        if (!cancelled && data.status === ResponseStatus.Success) {
          setStorage(data);
        }
      } catch (error) {
        log.error("Storage profile fetch failed: %o", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org.org_id]);

  const storagePct = storage
    ? (Number(storage.usage_bytes) / Number(storage.quota_bytes)) * 100
    : 0;
  const isFree = plan?.code === "free";
  // A cap is a denominator, so it earns a bar; an uncapped plan shows the count
  // alone rather than a fraction with nothing under the line.
  const seatCap = plan?.limits.seats ?? null;
  const seatPct = seatCap ? Math.min(100, (memberCount / seatCap) * 100) : 0;
  const price =
    plan && plan.price_cents > 0
      ? `${(plan.price_cents / 100).toFixed(2)} ${plan.currency} / ${plan.billing_period}`
      : null;

  return (
    <>
      <section className="flex flex-col">
        <SectionRule label="Plan" />
        {plan ? (
          <>
            <Row label="Current plan">
              <span className="text-fg-regular">{plan.name}</span>
              <Badge label={plan.code} className="shrink-0" />
            </Row>
            <Row label={price ? "Price" : "Since"}>
              <span className="text-fg-regular">
                {price ?? formatDayMonthYear(plan.started_at)}
              </span>
            </Row>
            {/* Nothing is being billed on a free plan, so the row would be a
                rule with no invoice behind it. */}
            {!org.is_personal && price !== null && (
              <Row label="Billed to">
                <span className="text-fg-regular">
                  The organization, not its members.
                </span>
              </Row>
            )}
          </>
        ) : (
          <span className="px-2 py-2 text-fg-muted-regular-small">
            {planLoaded
              ? "No plan period recorded for this organization."
              : "Loading plan…"}
          </span>
        )}
      </section>

      <section className="flex flex-col">
        <SectionRule label="Usage" />
        <div className="flex flex-col gap-1 px-2 py-2">
          <span className="flex items-baseline">
            <span className="flex-1 text-fg-muted-regular-small">Storage</span>
            <span
              className="font-mono text-fg-regular"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {storage
                ? `${storage.usage_human_readable} of ${storage.quota_human_readable}`
                : "—"}
            </span>
          </span>
          {storage && <QuotaProgressBar percentage={storagePct} size="md" />}
        </div>
        {!org.is_personal && (
          <div className="flex flex-col gap-1 px-2 py-2">
            <span className="flex items-baseline">
              <span className="flex-1 text-fg-muted-regular-small">Seats</span>
              <span
                className="font-mono text-fg-regular"
                style={{ fontSize: "var(--font-size-small)" }}
              >
                {seatCap
                  ? `${memberCount} of ${seatCap}`
                  : `${memberCount} in use`}
              </span>
            </span>
            {seatCap !== null && (
              <QuotaProgressBar percentage={seatPct} size="md" />
            )}
            {seatCap !== null && memberCount >= seatCap && (
              <span className="text-fg-muted-regular-small">
                All seats are in use; the next invitation is refused.
              </span>
            )}
          </div>
        )}
        {isFree && (
          <span className="px-2 text-fg-muted-regular-small">
            Free plan. Upgrade for additional storage.
          </span>
        )}
      </section>
    </>
  );
};
